<template>
    <LoadingCard v-if="loading"></LoadingCard>
    <div v-else>
        <div class="toolbar">
            <div class="toolbar__label">
                Chemicals available for use in spray diaries
            </div>
            <div class="actions">
                <i class="actions__item zmdi zmdi-search" @click="enableSearch"></i>
                <i class="actions__item far fa-check-square" v-if="onlySelected" @click="onlySelected=false"
                   v-b-tooltip="'Showing only selected items'"></i>
                <i class="actions__item far fa-square" v-else @click="onlySelected=true"
                   v-b-tooltip="'Show only selected items'"></i>
            </div>

            <div class="toolbar__search" :class="searchEnabled ? 'd-block' : 'd-none'">
                <input type="text" placeholder="Search..." ref="searchInput" v-model="searchTerm">
                <i class="toolbar__search__close zmdi zmdi-long-arrow-left" @click="disableSearch"></i>
            </div>
        </div>

        <div class="row">
            <div v-for="classification in filteredData" class="col-12">
                <div class="card">
                    <div class="card-header bg-primary text-white">
                        {{ classification.name}}
                    </div>

                    <b-table small hover stacked="md" :items="classification.commercialVariants" :fields="table.fields"
                             sort-by="name" :show-empty="true">
                        <template slot="activeIngredients" slot-scope="row">
                    <span class="badge-group mr-2" v-for="ingredient in [...row.item.activeIngredients].sort((a,b) => a.activeIngredient.name.localeCompare(b.activeIngredient.name))">
                        <span class="badge badge-secondary mb-1">{{ ingredient.activeIngredient.name }}</span><span class="badge badge-warning mb-1">{{ ingredient.ingredient_percentage }}%</span>
                    </span>
                        </template>
                        <template slot="name" slot-scope="row">
                            <div class="custom-control custom-checkbox">
                                <input class="custom-control-input"
                                       :id="`chemical${row.item.id}`" type="checkbox"
                                       :checked="linkedChemicals.includes(row.item.id)"
                                       @click.prevent="handleChemicalToggle(row.item)"
                                >
                                <label class="custom-control-label" :for="`chemical${row.item.id}`">&nbsp;</label>{{ row.item.name }}
                            </div>
                        </template>
                    </b-table>
                </div>
            </div>
        </div>
    </div>

</template>

<script>
import createLinkMutation from '@views/GrowerChemicals/ContractorBrowse/mutationCreateGrowerChemicalCommercialVariantLink.graphql';
import deleteLinkMutation from '@views/GrowerChemicals/ContractorBrowse/mutationDeleteGrowerChemicalCommercialVariantLink.graphql';
import listChemicalsQuery from '@views/GrowerChemicals/ContractorBrowse/queryListChemicalClassifications.graphql';
import listLinkedChemicalsQuery from '@views/GrowerChemicals/ContractorBrowse/queryListGrowerLinkedChemicalCommercialVariants.graphql';

export default {
  name: 'ContractorGrowerChemicalsBrowse',
  props: ['growerId'],
  components: {},
  apollo: {
    allChemicals: {
      query: listChemicalsQuery,
      update: data => data.filterChemicalClassifications.data
    },
    linkedChemicals: {
      query: listLinkedChemicalsQuery,
      update: data => data.filterGrowerChemicalCommercialVariantLinks.map(item => item.id),
      variables() {
        return {
          input: {
            user_id: this.growerId,
          },
        };
      },
    },
  },
  data() {
    return {
      allChemicals: [],
      linkedChemicals: [],
      searchEnabled: false,
      searchTerm: '',
      onlySelected: false,
      loading: 0,
    };
  },
  computed: {
    table() {
      return {
        fields: [
          {
            key: 'name',
            label: 'Commercial Variant',
            sortable: true,
          },
          {
            key: 'activeIngredients',
            label: 'Active Ingredients',
          },
        ],
      };
    },
    filteredData() {
      const subjectMatchesSearch = subject => {
        return (
          (this.onlySelected === false || this.linkedChemicals.includes(subject.id)) &&
          (this.searchTerm.length === 0 || subject.name.toLowerCase().includes(this.searchTerm.toLowerCase()))
        );
      };

      return this.allChemicals.map(classification => ({
          ...classification,
          commercialVariants: [...classification.commercialVariants.filter(subjectMatchesSearch)],
        }))
        .filter(classification => classification.commercialVariants.length > 0)
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  },
  methods: {
    enableSearch() {
      this.searchEnabled = true;
      this.$nextTick(() => this.$refs.searchInput.focus());
    },
    disableSearch() {
      this.searchTerm = '';
      this.searchEnabled = false;
    },
    handleChemicalToggle(chemical) {
      if (this.linkedChemicals.includes(chemical.id)) {
        this.unlinkChemical(chemical);
      } else {
        this.linkChemical(chemical);
      }
    },
    unlinkChemical(chemical) {
      this.$apollo.mutate({
        mutation: deleteLinkMutation,
        variables: {
          input: {
            user_id: this.growerId,
            id: chemical.id,
          },
        },
        update: (store, { data: { deleteGrowerChemicalCommercialVariantLink: unlinkedChemical } }) => {
          const queryKey = {
            query: listLinkedChemicalsQuery,
            variables: { input: { user_id: this.growerId } },
          };
          const data = store.readQuery(queryKey);
          const indexToRemove = data.filterGrowerChemicalCommercialVariantLinks.findIndex(item => item.id === unlinkedChemical.id);
          if (indexToRemove !== -1) {
            data.filterGrowerChemicalCommercialVariantLinks.splice(indexToRemove, 1);
            store.writeQuery({ ...queryKey, data });
          }
        },
        optimisticResponse: {
          __typename: 'Mutation',
          deleteGrowerChemicalCommercialVariantLink: {
            __typename: 'ChemicalCommercialVariant',
            ...chemical,
          },
        },
      });
    },
    linkChemical(chemical) {
      this.$apollo.mutate({
        mutation: createLinkMutation,
        variables: {
          input: {
            id: chemical.id,
            user_id: this.growerId,
          },
        },
        update: (store, { data: { createGrowerChemicalCommercialVariantLink: linkedChemical } }) => {
          const queryKey = {
            query: listLinkedChemicalsQuery,
            variables: { input: { user_id: this.growerId } },
          };
          const data = store.readQuery(queryKey);
          data.filterGrowerChemicalCommercialVariantLinks.push(linkedChemical);
          store.writeQuery({ ...queryKey, data });
        },
        optimisticResponse: {
          __typename: 'Mutation',
          createGrowerChemicalCommercialVariantLink: {
            __typename: 'ChemicalCommercialVariant',
            ...chemical,
          },
        },
      });
    },
  },
};
</script>
